/** @jsx jsx */
import { Fragment, useState } from 'react'
import { Link } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { Box, Button, Flex, jsx, Text } from 'theme-ui'
import moment from 'moment'
import TruncateMarkup from 'react-truncate-markup'
import ReviewStars from '~/components/Reviews/Stars'
import { useCatalog } from '~/hooks/components/use-catalog'
import { getPackUrl } from '~/utils/packs'

const Review = ({ review, showListing }) => {
  const { author, body, date, productSlug, score, title, verified } = review

  const { catalog } = useCatalog()
  const listing = [...catalog.packs, ...catalog.merch].find(
    ({ slug }) => slug === productSlug
  )

  const lines = listing ? 6 : 9
  const untruncatedLines = 1000
  const [truncationLines, setTruncationLines] = useState(lines)

  const readMore = (rootEl) => {
    return (
      <Fragment>
        <Text>... </Text>
        <Button
          onClick={() => setTruncationLines(untruncatedLines)}
          variant="reset"
          pr={1}
          sx={{ display: 'block' }}
        >
          <Text
            variant="body3"
            color="brandPrimary"
            sx={{ textDecoration: 'underline' }}
          >
            {'Read more'}
          </Text>
        </Button>
      </Fragment>
    )
  }

  return (
    <Flex
      bg="white"
      px={2}
      py={3}
      sx={{ borderRadius: '10px', flexDirection: 'column' }}
    >
      <Flex
        mb={1}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <ReviewStars score={score} />
        <Text color="greyDark" variant="description">
          {moment(date).format('l')}
        </Text>
      </Flex>
      {title && (
        <Text as="h3" variant="subtitle2" mb={2}>
          {title}
        </Text>
      )}
      <Box sx={{ flexGrow: '1' }}>
        <TruncateMarkup lines={truncationLines} ellipsis={readMore}>
          <Text as="p" variant="quote2">
            {body}
          </Text>
        </TruncateMarkup>
      </Box>
      {listing && showListing && (
        <Flex mt={[2, 3]} sx={{ alignItems: 'center' }}>
          {listing.mainImage && (
            <Box mr={2} sx={{ flexShrink: '0' }}>
              <Link
                to={getPackUrl(listing)}
                aria-label={`Shop ${listing.name}`}
              >
                <GatsbyImage
                  image={listing?.mainImage.gatsbyImageData}
                  alt={listing?.mainImage.title}
                  sx={{
                    borderRadius: '50%',
                    overflow: 'hidden',
                    height: '72px',
                    width: '72px',
                  }}
                />
              </Link>
            </Box>
          )}
          <Box sx={{ a: { variant: 'text.textLink' } }}>
            <Link to={getPackUrl(listing)}>{`Shop ${listing.name}`}</Link>
          </Box>
        </Flex>
      )}
      {author && (
        <Box
          mt={3}
          pt={2}
          sx={{
            borderTop: '1px solid',
            borderTopColor: 'greyLight',
          }}
        >
          <Text as="p" variant="callout1" my={0}>
            {author}
          </Text>
          <Text as="p" variant="description" color="greyDark" my={0}>
            {verified ? 'Verified Buyer' : 'Verified Reviewer'}
          </Text>
        </Box>
      )}
    </Flex>
  )
}

export default Review
